import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  fetchPlayersWithRegistrations,
  checkInParticipations,
  uncheckInParticipation,
} from "../../../features/player/playerSlice";
import PlayerSelector from "../PlayerSelector";
import { useAppDispatch } from "../../../store";
import { Participation } from "../../../types/Participation";
import { currentDate } from "../../../helpers/currentDate";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
  Switch,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Tooltip,
  Paper,
  Fade,
  Zoom,
  useTheme,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaidIcon from "@mui/icons-material/CheckCircleOutline";
import NotPaidIcon from "@mui/icons-material/HighlightOff";
import { updateDoc, doc } from "firebase/firestore";
import firestoreInstance from "../../../services/firestoreInstance";
import { useNavigate } from "react-router-dom";

interface CheckinProps {}

const Checkin: React.FC<CheckinProps> = () => {
  console.log("🔄 [DEBUG] Checkin component rendering");

  const dispatch = useAppDispatch();
  const playerSelectorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();

  //state with current playerId
  const [currentPlayerId, setCurrentPlayerId] = useState<string | null>(null);
  //state with current player participations
  const [currentPlayerParticipations, setCurrentPlayerParticipations] =
    useState<Participation[]>([]);
  const [selectedParticipationIds, setSelectedParticipationIds] = useState<
    string[]
  >([]);
  const [allowOtherDays, setAllowOtherDays] = useState(false);
  const [snackBarOpened, setSnackBarOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recentlyCheckedIn, setRecentlyCheckedIn] = useState<
    Array<{ name: string; surname: string; tableaux: string[] }>
  >([]);
  const [checkedInCount, setCheckedInCount] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [participationToUncheck, setParticipationToUncheck] = useState<
    string | null
  >(null);
  const [snackBarUndoOpened, setSnackBarUndoOpened] = useState(false);
  const [confirmOtherDayDialogOpen, setConfirmOtherDayDialogOpen] =
    useState(false);
  const [pendingParticipationId, setPendingParticipationId] = useState<
    string | null
  >(null);
  const [isPaying, setIsPaying] = useState(false);
  const [snackBarPaymentOpened, setSnackBarPaymentOpened] = useState(false);
  const [paidCount, setPaidCount] = useState(0);
  const [expanded, setExpanded] = useState<string | false>("");

  // Traces pour les changements d'état
  useEffect(() => {
    console.log("🔄 [DEBUG] currentPlayerId changed:", currentPlayerId);
  }, [currentPlayerId]);

  useEffect(() => {
    console.log("🔄 [DEBUG] currentPlayerParticipations changed:", {
      length: currentPlayerParticipations.length,
      ids: currentPlayerParticipations.map((p) => p.id),
    });
  }, [currentPlayerParticipations]);

  useEffect(() => {
    console.log(
      "🔄 [DEBUG] selectedParticipationIds changed:",
      selectedParticipationIds
    );
  }, [selectedParticipationIds]);

  const openSnackBar = () => setSnackBarOpened(true);
  const handleCloseSnackBar = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackBarOpened(false);
  };

  const handleCheckin = async () => {
    if (selectedParticipationIds.length > 0) {
      setIsLoading(true);
      try {
        await dispatch(checkInParticipations(selectedParticipationIds));
        // Stocker le nombre d'inscriptions pointées
        setCheckedInCount(selectedParticipationIds.length);
        // Ajouter aux récemment pointés
        const checkedInPlayer = currentPlayerParticipations[0]?.players;
        if (checkedInPlayer) {
          const tableaux = currentPlayerParticipations
            .filter((p) => selectedParticipationIds.includes(p.id!))
            .map((p) => p.tableau.name);
          setRecentlyCheckedIn((prev) =>
            [
              {
                name: checkedInPlayer.name,
                surname: checkedInPlayer.surname,
                tableaux,
              },
              ...prev,
            ].slice(0, 5)
          );
        }
        setSelectedParticipationIds([]);
        setCurrentPlayerId(null);
        setCurrentPlayerParticipations([]);
        openSnackBar();
        // Focus sur le champ de recherche pour le prochain pointage
        playerSelectorRef.current?.querySelector("input")?.focus();
      } catch (error) {
        console.error("Erreur lors du check-in:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleUncheck = async () => {
    if (participationToUncheck) {
      setIsLoading(true);
      try {
        await dispatch(uncheckInParticipation(participationToUncheck));
        setSnackBarUndoOpened(true);
        setParticipationToUncheck(null);
        setConfirmDialogOpen(false);
        // Mettre à jour localement la participation
        setCurrentPlayerParticipations((prev) =>
          prev.map((p) =>
            p.id === participationToUncheck
              ? { ...p, checkedIn: false, checkedInAt: undefined }
              : p
          )
        );
      } catch (error) {
        console.error("Erreur lors de l'annulation du pointage:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    console.log("🔄 [DEBUG] fetchPlayersWithRegistrations useEffect triggered");
    dispatch(fetchPlayersWithRegistrations());
  }, [dispatch]);

  // Initialiser expanded avec la date d'aujourd'hui une seule fois au montage
  useEffect(() => {
    console.log("🔄 [DEBUG] expanded initialization useEffect triggered");
    const todayKey = currentDate().toLocaleDateString("fr-FR");
    setExpanded(todayKey);
  }, []); // Tableau de dépendances vide = exécution unique au montage

  // Calculer todayKey une seule fois avec useMemo
  const todayKey = useMemo(() => {
    console.log("🔄 [DEBUG] todayKey useMemo recalculating");
    return currentDate().toLocaleDateString("fr-FR");
  }, []);

  // Sélectionner automatiquement tous les tableaux disponibles du jour
  useEffect(() => {
    console.log("🔄 [DEBUG] currentPlayerParticipations useEffect triggered", {
      participationsLength: currentPlayerParticipations.length,
      selectedIdsLength: selectedParticipationIds.length,
    });

    if (currentPlayerParticipations.length > 0) {
      const availableToday = currentPlayerParticipations
        .filter((p) => {
          const rawDate = p.tableau?.date as any;
          const dateObj =
            rawDate instanceof Date ? rawDate : rawDate?.toDate?.();
          return (
            dateObj?.toDateString() === currentDate().toDateString() &&
            !p.checkedIn
          );
        })
        .map((p) => p.id!);

      console.log("🔄 [DEBUG] availableToday calculated", availableToday);

      // Éviter la mise à jour si les IDs sont identiques
      setSelectedParticipationIds((prev) => {
        const newIds = availableToday;
        if (
          prev.length === newIds.length &&
          prev.every((id) => newIds.includes(id))
        ) {
          console.log(
            "🔄 [DEBUG] No change in selectedParticipationIds, keeping previous"
          );
          return prev; // Pas de changement
        }
        console.log("🔄 [DEBUG] Updating selectedParticipationIds", {
          prev,
          newIds,
        });
        return newIds;
      });
    }
  }, [currentPlayerParticipations]);

  // Regrouper les participations par date
  const participationsByDate = useMemo(() => {
    console.log("🔄 [DEBUG] participationsByDate useMemo recalculating", {
      participationsLength: currentPlayerParticipations.length,
    });
    return currentPlayerParticipations.reduce((acc, p) => {
      const rawDate = p.tableau?.date as any;
      const dateObj = rawDate instanceof Date ? rawDate : rawDate?.toDate?.();
      const dateKey = dateObj
        ? dateObj.toLocaleDateString("fr-FR")
        : "Inconnue";
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(p);
      return acc;
    }, {} as Record<string, Participation[]>);
  }, [currentPlayerParticipations]);

  const dateKeys = useMemo(() => {
    return Object.keys(participationsByDate).sort((a, b) => {
      // Trier par date croissante
      const [da, ma, ya] = a.split("/").map(Number);
      const [db, mb, yb] = b.split("/").map(Number);
      return (
        new Date(ya, ma - 1, da).getTime() - new Date(yb, mb - 1, db).getTime()
      );
    });
  }, [participationsByDate]);

  // Handler pour sélectionner une participation
  const handleSelectParticipation = (p: Participation, isToday: boolean) => {
    const isSelected = selectedParticipationIds.includes(p.id!);
    if (!isToday && !isSelected) {
      setPendingParticipationId(p.id!);
      setConfirmOtherDayDialogOpen(true);
    } else {
      setSelectedParticipationIds((prev) =>
        isSelected ? prev.filter((id) => id !== p.id) : [...prev, p.id!]
      );
    }
  };
  const handleConfirmOtherDay = () => {
    if (pendingParticipationId) {
      setSelectedParticipationIds((prev) =>
        prev.includes(pendingParticipationId)
          ? prev.filter((id) => id !== pendingParticipationId)
          : [...prev, pendingParticipationId]
      );
      setPendingParticipationId(null);
      setConfirmOtherDayDialogOpen(false);
    }
  };
  const handleCancelOtherDay = () => {
    setPendingParticipationId(null);
    setConfirmOtherDayDialogOpen(false);
  };

  // Calcul du montant total dû
  function calculatePriceFromBoards(boards: any[]): number {
    let nbSelected = boards.length;
    let hasFullPrice = boards.some((board) => board?.fullPrice);
    if (nbSelected === 1) {
      return hasFullPrice ? 10 : 9;
    } else if (nbSelected === 2) {
      return hasFullPrice ? 18 : 16;
    } else if (nbSelected === 3) {
      return hasFullPrice ? 26 : 25;
    } else if (nbSelected === 4) {
      return hasFullPrice ? 34 : 32;
    }
    return 0;
  }

  const unpaidParticipations = useMemo(
    () =>
      currentPlayerParticipations.filter(
        (p) =>
          !(p.paymentStatus === "admin" || p.paymentStatus === "registration")
      ),
    [currentPlayerParticipations]
  );

  const paidParticipations = useMemo(
    () =>
      currentPlayerParticipations.filter(
        (p) => p.paymentStatus === "admin" || p.paymentStatus === "registration"
      ),
    [currentPlayerParticipations]
  );

  const paidBoards = useMemo(
    () => paidParticipations.map((p) => p.tableau),
    [paidParticipations]
  );
  const allBoards = useMemo(
    () => currentPlayerParticipations.map((p) => p.tableau),
    [currentPlayerParticipations]
  );

  const totalAll = useMemo(
    () => calculatePriceFromBoards(allBoards),
    [allBoards]
  );
  const totalPaid = useMemo(
    () => calculatePriceFromBoards(paidBoards),
    [paidBoards]
  );
  const totalDue = useMemo(() => totalAll - totalPaid, [totalAll, totalPaid]);

  // Handler pour marquer comme payé
  const handleMarkAsPaid = async () => {
    setIsPaying(true);
    try {
      console.log(
        "Paiement validé pour participations :",
        unpaidParticipations.map((p) => p.id)
      );
      await Promise.all(
        unpaidParticipations.map(async (p) => {
          try {
            await updateDoc(
              doc(firestoreInstance, "participation", p.id, "private", "data"),
              {
                paymentStatus: "admin",
              }
            );
          } catch (err) {
            console.error("Erreur Firestore pour participation", p.id, err);
          }
        })
      );
      // Mise à jour locale immédiate
      setCurrentPlayerParticipations((prev) =>
        prev.map((part) =>
          unpaidParticipations.some((up) => up.id === part.id)
            ? { ...part, paymentStatus: "admin" }
            : part
        )
      );
      setPaidCount(unpaidParticipations.length);
      setSnackBarPaymentOpened(true);
      await dispatch(fetchPlayersWithRegistrations()).unwrap();
    } catch (e) {
      console.error(e);
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Stack spacing={3}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}22, ${theme.palette.secondary.main}22)`,
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2, height: 56 }}
            >
              <Box sx={{ flexGrow: 1, height: "100%" }} ref={playerSelectorRef}>
                <PlayerSelector
                  label="Rechercher un joueur (nom, prénom, licence)"
                  currentPlayerId={currentPlayerId}
                  withRegistration={true}
                  onPlayerSelect={(player) => {
                    console.log("🔄 [DEBUG] onPlayerSelect called", {
                      playerId: player?.id,
                      hasParticipations: !!player?.participation,
                      participationsLength: player?.participation?.length,
                    });

                    if (!player) {
                      console.log("🔄 [DEBUG] Clearing player data");
                      setCurrentPlayerId(null);
                      setCurrentPlayerParticipations([]);
                      setSelectedParticipationIds([]);
                      return;
                    }
                    console.log("🔄 [DEBUG] Setting player data", player.id);
                    setCurrentPlayerId(player?.id || null);
                    const participations = player.participation ?? [];
                    setCurrentPlayerParticipations(participations);
                  }}
                />
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", height: "100%" }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ minWidth: 200, height: 40 }}
                  onClick={() => {
                    if (currentPlayerId) {
                      navigate(`/register/select?playerId=${currentPlayerId}`);
                    } else {
                      navigate("/register");
                    }
                  }}
                >
                  {currentPlayerId
                    ? "Modifier les inscriptions"
                    : "Inscrire un joueur"}
                </Button>
              </Box>
              <Tooltip title="Permet de pointer les joueurs pour les tableaux des autres jours">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    whiteSpace: "nowrap",
                    gap: 0.5,
                  }}
                >
                  <Switch
                    checked={allowOtherDays}
                    onChange={(e) => setAllowOtherDays(e.target.checked)}
                  />
                  <Typography sx={{ display: "flex", alignItems: "center" }}>
                    Autres jours
                    <Box
                      component="span"
                      sx={{ display: "flex", alignItems: "center", ml: 0.3 }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="16"
                        viewBox="0 0 24 24"
                        width="16"
                        fill="#C79A61"
                        style={{ verticalAlign: "middle" }}
                      >
                        <path d="M0 0h24v24H0z" fill="none" />
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-7h2v5h-2zm0-6h2v2h-2z" />
                      </svg>
                    </Box>
                  </Typography>
                </Box>
              </Tooltip>
            </Stack>

            {recentlyCheckedIn.length > 0 && (
              <Fade in={true}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  >
                    <HowToRegIcon /> Derniers joueurs pointés
                  </Typography>
                  <Stack spacing={1}>
                    {recentlyCheckedIn.map((player, index) => (
                      <Zoom
                        in={true}
                        style={{
                          transitionDelay: `${index * 100}ms`,
                        }}
                        key={index}
                      >
                        <Paper
                          elevation={2}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(45deg, ${theme.palette.success.light}22, ${theme.palette.success.main}22)`,
                            border: `1px solid ${theme.palette.success.main}44`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CheckCircleIcon
                              sx={{ color: theme.palette.success.main }}
                            />
                            <strong>
                              {player.name} {player.surname}
                            </strong>{" "}
                            - {player.tableaux.join(", ")}
                          </Typography>
                        </Paper>
                      </Zoom>
                    ))}
                  </Stack>
                </Box>
              </Fade>
            )}

            {currentPlayerParticipations.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                {dateKeys.map((dateKey) => (
                  <Accordion
                    key={dateKey}
                    expanded={expanded === dateKey}
                    onChange={(_, isExpanded) =>
                      setExpanded(isExpanded ? dateKey : false)
                    }
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography variant="h6">
                          Participations du {dateKey}
                          {dateKey === todayKey && " (aujourd'hui)"}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid2 container spacing={2}>
                        {participationsByDate[dateKey].map(
                          (p: Participation) => {
                            const rawDate = p.tableau?.date as any;
                            const dateObj =
                              rawDate instanceof Date
                                ? rawDate
                                : rawDate?.toDate?.();
                            const isToday =
                              dateObj?.toDateString() ===
                              currentDate().toDateString();
                            const disabled = !isToday && !allowOtherDays;
                            const selected = selectedParticipationIds.includes(
                              p.id!
                            );
                            const isCheckedIn = p.checkedIn;
                            const isPaid =
                              p.paymentStatus === "admin" ||
                              p.paymentStatus === "registration";
                            console.log(
                              "Rendu carte participation",
                              p.id,
                              "isCheckedIn:",
                              isCheckedIn
                            );

                            return (
                              <Grid2 key={p.id} xs={12} sm={6} md={4}>
                                <Zoom in={true}>
                                  <Card
                                    elevation={8}
                                    sx={{
                                      opacity: !isPaid ? 0.5 : 1,
                                      pointerEvents: disabled ? "none" : "auto",
                                      backgroundColor: isCheckedIn
                                        ? theme.palette.success.light
                                        : disabled && selected
                                        ? theme.palette.warning.light
                                        : disabled
                                        ? theme.palette.grey[200]
                                        : selected
                                        ? theme.palette.primary.main
                                        : theme.palette.background.paper,
                                      color: disabled
                                        ? theme.palette.text.disabled
                                        : selected || isCheckedIn
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.text.primary,
                                      borderRadius: 3,
                                      transition: "all 0.3s ease",
                                      "&:hover": {
                                        transform: "scale(1.03)",
                                        boxShadow: 6,
                                      },
                                      position: "relative",
                                    }}
                                  >
                                    {/* Indicateur de paiement */}
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 8,
                                        right: 20,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      {isPaid ? (
                                        <PaidIcon
                                          color="success"
                                          fontSize="small"
                                        />
                                      ) : (
                                        <NotPaidIcon
                                          color="error"
                                          fontSize="small"
                                        />
                                      )}
                                      <Typography
                                        variant="caption"
                                        color={
                                          isPaid ? "success.main" : "error.main"
                                        }
                                      >
                                        {isPaid ? "Payée" : "Non payée"}
                                      </Typography>
                                    </Box>
                                    {/* Icône de poubelle en overlay si pointé */}
                                    {isCheckedIn && (
                                      <Tooltip title="Annuler le pointage">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            console.log(
                                              "Icône poubelle cliquée pour participation",
                                              p.id
                                            );
                                            setParticipationToUncheck(p.id!);
                                            setConfirmDialogOpen(true);
                                          }}
                                          sx={{
                                            position: "absolute",
                                            top: 40,
                                            right: 20,
                                            zIndex: 2,
                                            background: "rgba(255,255,255,0.7)",
                                          }}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <CardActionArea
                                      onClick={() =>
                                        handleSelectParticipation(p, isToday)
                                      }
                                      disabled={
                                        disabled || isCheckedIn || !isPaid
                                      }
                                    >
                                      <CardContent sx={{ padding: 2 }}>
                                        <Stack spacing={1}>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <EmojiEventsIcon /> Tableau{" "}
                                            {p.tableau!.name}
                                          </Typography>

                                          <Typography variant="body1">
                                            {p.tableau!.label}
                                          </Typography>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              color: theme.palette.warning.main,
                                            }}
                                          >
                                            <AccessTimeIcon /> Heure limite de
                                            pointage : {p.tableau!.heureLimite}
                                          </Typography>
                                          {isCheckedIn && (
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                color:
                                                  theme.palette.success.main,
                                              }}
                                            >
                                              <CheckCircleIcon /> Pointé le{" "}
                                              {p.checkedInAt
                                                ? (() => {
                                                    const dateObj =
                                                      typeof (
                                                        p.checkedInAt as any
                                                      )?.toDate === "function"
                                                        ? (
                                                            p.checkedInAt as any
                                                          ).toDate()
                                                        : p.checkedInAt;
                                                    const dateStr =
                                                      dateObj.toLocaleDateString(
                                                        "fr-FR",
                                                        {
                                                          day: "2-digit",
                                                          month: "2-digit",
                                                          year: "numeric",
                                                        }
                                                      );
                                                    const timeStr =
                                                      dateObj.toLocaleTimeString(
                                                        "fr-FR",
                                                        {
                                                          hour: "2-digit",
                                                          minute: "2-digit",
                                                        }
                                                      );
                                                    return `${dateStr} à ${timeStr}`;
                                                  })()
                                                : ""}
                                            </Typography>
                                          )}
                                        </Stack>
                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                </Zoom>
                              </Grid2>
                            );
                          }
                        )}
                      </Grid2>
                    </AccordionDetails>
                  </Accordion>
                ))}
                <Stack
                  direction="row"
                  justifyContent="center"
                  mt={2}
                  sx={{ width: "100%" }}
                >
                  <Button
                    variant="contained"
                    disabled={
                      selectedParticipationIds.length === 0 || isLoading
                    }
                    onClick={handleCheckin}
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <HowToRegIcon />
                      )
                    }
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    {isLoading ? "Pointage en cours..." : "Pointer"}
                  </Button>
                </Stack>
                {unpaidParticipations.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                      mt: 1,
                      p: 2,
                      bgcolor: "warning.light",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      color="error.main"
                      fontWeight={600}
                    >
                      Montant total à régler : {totalDue} €
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsPaid();
                      }}
                      disabled={isPaying}
                      startIcon={
                        isPaying ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      Marquer comme payé
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
      <Snackbar
        open={snackBarOpened}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={handleCloseSnackBar}
      >
        <Alert
          onClose={handleCloseSnackBar}
          severity="success"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleIcon />
          {`Pointage validé pour ${checkedInCount} inscription(s)`}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Annuler le pointage</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voulez-vous vraiment annuler le pointage de cette participation ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button
            onClick={handleUncheck}
            color="error"
            variant="contained"
            autoFocus
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackBarUndoOpened}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={() => setSnackBarUndoOpened(false)}
      >
        <Alert
          onClose={() => setSnackBarUndoOpened(false)}
          severity="info"
          sx={{ width: "100%" }}
        >
          Pointage annulé avec succès !
        </Alert>
      </Snackbar>
      {/* Dialog de confirmation pour pointage hors jour */}
      <Dialog open={confirmOtherDayDialogOpen} onClose={handleCancelOtherDay}>
        <DialogTitle>Pointer un autre jour</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Il est préférable de pointer le jour du tableau. Voulez-vous
            vraiment pointer ce joueur pour ce tableau aujourd'hui&nbsp;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOtherDay} color="primary">
            Annuler
          </Button>
          <Button
            onClick={handleConfirmOtherDay}
            color="warning"
            variant="contained"
            autoFocus
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackBarPaymentOpened}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={() => setSnackBarPaymentOpened(false)}
      >
        <Alert
          onClose={() => setSnackBarPaymentOpened(false)}
          severity="success"
          sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}
        >
          Paiement validé pour {paidCount} inscription(s)
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Checkin;
